'use client';

import mammothDefault from 'mammoth';
import {
  AlignmentType,
  Document,
  HeadingLevel,
  ImageRun,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';
import { jsPDF } from 'jspdf';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';

const mammoth = mammothDefault as unknown as {
  convertToHtml: (
    input: { arrayBuffer: ArrayBuffer },
    options?: {
      convertImage?: unknown;
    },
  ) => Promise<{ value: string; messages: Array<{ type?: string; message?: string }> }>;
  images: {
    inline: (
      mapper: (element: {
        contentType?: string;
        read: (encoding: 'base64') => Promise<string>;
      }) => Promise<{ src: string }>,
    ) => unknown;
  };
};

export type SimplePdfBlock =
  | { kind: 'heading'; text: string; level: number }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list-item'; text: string; ordered: boolean }
  | { kind: 'table'; rows: string[][] }
  | { kind: 'image'; src: string; alt: string };

export type WorkbookSheetPreview = {
  name: string;
  rows: string[][];
};

export type PptxSlidePreview = {
  id: string;
  title: string;
  textLines: string[];
  images: Array<{ src: string; alt: string }>;
};

export function isDocxFile(file: File) {
  return (
    file.name.toLowerCase().endsWith('.docx') ||
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  );
}

export function isXlsxFile(file: File) {
  return (
    file.name.toLowerCase().endsWith('.xlsx') ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
}

export function isPptxFile(file: File) {
  return (
    file.name.toLowerCase().endsWith('.pptx') ||
    file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  );
}

export async function readDocxHtml(file: File) {
  const result = await mammoth.convertToHtml(
    { arrayBuffer: await file.arrayBuffer() },
    {
      convertImage: mammoth.images.inline(async (element) => {
        const base64 = await element.read('base64');
        return {
          src: `data:${element.contentType || 'image/png'};base64,${base64}`,
        };
      }),
    },
  );

  return {
    html: result.value,
    messages: result.messages.map((item) => item.message || '').filter(Boolean),
  };
}

function readNodeText(node: Node) {
  return (node.textContent || '').replace(/\s+/g, ' ').trim();
}

export function parseWordHtmlToBlocks(html: string) {
  const parser = new DOMParser();
  const document = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = document.body.firstElementChild;
  const blocks: SimplePdfBlock[] = [];

  if (!root) {
    return blocks;
  }

  for (const child of Array.from(root.children)) {
    const tag = child.tagName.toLowerCase();

    if (/^h[1-6]$/.test(tag)) {
      const level = Number(tag.slice(1));
      const text = readNodeText(child);
      if (text) {
        blocks.push({ kind: 'heading', text, level });
      }
      continue;
    }

    if (tag === 'p') {
      const image = child.querySelector('img');
      const text = readNodeText(child);
      if (image?.getAttribute('src')) {
        blocks.push({
          kind: 'image',
          src: image.getAttribute('src') || '',
          alt: image.getAttribute('alt') || '',
        });
      }
      if (text) {
        blocks.push({ kind: 'paragraph', text });
      }
      continue;
    }

    if (tag === 'ul' || tag === 'ol') {
      for (const item of Array.from(child.querySelectorAll('li'))) {
        const text = readNodeText(item);
        if (text) {
          blocks.push({ kind: 'list-item', text, ordered: tag === 'ol' });
        }
      }
      continue;
    }

    if (tag === 'table') {
      const rows = Array.from(child.querySelectorAll('tr'))
        .map((row) => Array.from(row.querySelectorAll('th,td')).map((cell) => readNodeText(cell)))
        .filter((row) => row.some(Boolean));

      if (rows.length) {
        blocks.push({ kind: 'table', rows });
      }
      continue;
    }

    if (tag === 'img' && child.getAttribute('src')) {
      blocks.push({
        kind: 'image',
        src: child.getAttribute('src') || '',
        alt: child.getAttribute('alt') || '',
      });
    }
  }

  return blocks;
}

async function dataUrlToImageDimensions(src: string) {
  const image = new Image();
  image.decoding = 'async';

  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Image could not be loaded.'));
    image.src = src;
  });

  return image;
}

export async function exportWordBlocksToPdf(blocks: SimplePdfBlock[]) {
  const pdf = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 42;
  const maxWidth = pageWidth - margin * 2;
  let cursorY = margin;

  const ensureSpace = (neededHeight: number) => {
    if (cursorY + neededHeight <= pageHeight - margin) {
      return;
    }

    pdf.addPage();
    cursorY = margin;
  };

  for (const block of blocks) {
    if (block.kind === 'heading') {
      const fontSize = Math.max(16, 28 - block.level * 2.2);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(fontSize);
      const lines = pdf.splitTextToSize(block.text, maxWidth);
      ensureSpace(lines.length * (fontSize + 2));
      pdf.text(lines, margin, cursorY);
      cursorY += lines.length * (fontSize + 3) + 10;
      continue;
    }

    if (block.kind === 'paragraph' || block.kind === 'list-item') {
      const prefix = block.kind === 'list-item' ? (block.ordered ? '1. ' : '• ') : '';
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11.5);
      const lines = pdf.splitTextToSize(`${prefix}${block.text}`, maxWidth);
      ensureSpace(lines.length * 16);
      pdf.text(lines, margin, cursorY);
      cursorY += lines.length * 16 + 6;
      continue;
    }

    if (block.kind === 'table') {
      const columnCount = Math.max(...block.rows.map((row) => row.length), 1);
      const columnWidth = maxWidth / columnCount;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);

      for (const row of block.rows) {
        const rowHeight = 34;
        ensureSpace(rowHeight + 4);

        row.forEach((cell, index) => {
          const x = margin + index * columnWidth;
          pdf.rect(x, cursorY - 12, columnWidth, rowHeight);
          const textLines = pdf.splitTextToSize(cell || '', columnWidth - 8);
          pdf.text(textLines, x + 4, cursorY + 2);
        });

        cursorY += rowHeight;
      }

      cursorY += 10;
      continue;
    }

    if (block.kind === 'image' && block.src) {
      try {
        const image = await dataUrlToImageDimensions(block.src);
        const ratio = image.width / Math.max(1, image.height);
        const drawWidth = Math.min(maxWidth, image.width);
        const drawHeight = drawWidth / Math.max(0.1, ratio);
        ensureSpace(drawHeight + 12);
        const format = block.src.includes('image/png') ? 'PNG' : 'JPEG';
        pdf.addImage(block.src, format, margin, cursorY, drawWidth, drawHeight);
        cursorY += drawHeight + 12;
      } catch {
        continue;
      }
    }
  }

  return pdf.output('blob');
}

export async function createDocxBlobFromTextSections(
  title: string,
  sections: Array<{
    heading?: string;
    paragraphs: string[];
  }>,
) {
  const children: Array<Paragraph | Table> = [];

  if (title.trim()) {
    children.push(
      new Paragraph({
        text: title.trim(),
        heading: HeadingLevel.TITLE,
        spacing: { after: 200 },
      }),
    );
  }

  for (const section of sections) {
    if (section.heading) {
      children.push(
        new Paragraph({
          text: section.heading,
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 160, after: 120 },
        }),
      );
    }

    for (const paragraph of section.paragraphs) {
      children.push(
        new Paragraph({
          children: [new TextRun(paragraph)],
          spacing: { after: 120 },
        }),
      );
    }
  }

  const document = new Document({
    sections: [
      {
        children,
      },
    ],
  });

  return Packer.toBlob(document);
}

export async function createDocxBlobFromSignatureImage(params: {
  title: string;
  signatureDataUrl?: string | null;
  typedName?: string | null;
}) {
  const children: Paragraph[] = [
    new Paragraph({
      text: params.title,
      heading: HeadingLevel.TITLE,
      spacing: { after: 240 },
    }),
  ];

  if (params.typedName?.trim()) {
    children.push(
      new Paragraph({
        text: params.typedName.trim(),
        spacing: { after: 160 },
      }),
    );
  }

  if (params.signatureDataUrl) {
    const response = await fetch(params.signatureDataUrl);
    const buffer = await response.arrayBuffer();
    const imageType = params.signatureDataUrl.includes('image/jpeg') ? 'jpg' : 'png';
    children.push(
      new Paragraph({
        children: [
          new ImageRun({
            data: buffer,
            type: imageType,
            transformation: { width: 280, height: 90 },
          }),
        ],
        alignment: AlignmentType.LEFT,
      }),
    );
  }

  const document = new Document({
    sections: [{ children }],
  });

  return Packer.toBlob(document);
}

export async function readWorkbookPreview(file: File) {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheets: WorkbookSheetPreview[] = workbook.SheetNames.map((name) => ({
    name,
    rows: (
      XLSX.utils.sheet_to_json(workbook.Sheets[name], {
        header: 1,
        raw: false,
        defval: '',
        blankrows: false,
      }) as string[][]
    ).slice(0, 120),
  }));

  return { workbook, sheets };
}

export function buildSheetHtml(sheet: WorkbookSheetPreview) {
  const rows = sheet.rows
    .map(
      (row) =>
        `<tr>${row
          .map(
            (cell) =>
              `<td>${String(cell ?? '')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')}</td>`,
          )
          .join('')}</tr>`,
    )
    .join('');

  return `<table><thead><tr><th colspan="${Math.max(
    sheet.rows[0]?.length || 1,
    1,
  )}">${sheet.name}</th></tr></thead><tbody>${rows}</tbody></table>`;
}

export async function exportSheetToPdf(params: {
  rows: string[][];
  sheetName: string;
  orientation: 'portrait' | 'landscape';
  pageSize: 'a4' | 'letter';
}) {
  const pdf = new jsPDF({
    unit: 'pt',
    format: params.pageSize,
    orientation: params.orientation === 'landscape' ? 'l' : 'p',
    compress: true,
  });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 32;
  const maxWidth = pageWidth - margin * 2;
  const columnCount = Math.max(...params.rows.map((row) => row.length), 1);
  const columnWidth = maxWidth / columnCount;
  let cursorY = margin + 24;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text(params.sheetName, margin, margin);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9.5);

  for (const row of params.rows) {
    const rowHeight = 28;
    if (cursorY + rowHeight > pageHeight - margin) {
      pdf.addPage();
      cursorY = margin;
    }

    row.forEach((cell, index) => {
      const x = margin + index * columnWidth;
      pdf.rect(x, cursorY - 11, columnWidth, rowHeight);
      const lines = pdf.splitTextToSize(String(cell ?? ''), columnWidth - 6);
      pdf.text(lines, x + 3, cursorY + 3);
    });

    cursorY += rowHeight;
  }

  return pdf.output('blob');
}

export function createWorkbookBlobFromRows(rows: string[][], sheetName: string) {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  const array = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
  return new Blob([array], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export function createCsvBlobFromRows(rows: string[][]) {
  const csv = rows
    .map((row) =>
      row
        .map((cell) => {
          const value = String(cell ?? '');
          return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
        })
        .join(','),
    )
    .join('\n');

  return new Blob([csv], { type: 'text/csv;charset=utf-8' });
}

function parseSlideText(xml: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'application/xml');
  return Array.from(doc.getElementsByTagName('a:t'))
    .map((node) => readNodeText(node))
    .filter(Boolean);
}

async function readSlideImages(zip: JSZip, slidePath: string) {
  const relPath = slidePath.replace('slides/', 'slides/_rels/') + '.rels';
  const relFile = zip.file(`ppt/${relPath}`);

  if (!relFile) {
    return [] as Array<{ src: string; alt: string }>;
  }

  const relXml = await relFile.async('string');
  const relDoc = new DOMParser().parseFromString(relXml, 'application/xml');
  const imageTargets = Array.from(relDoc.getElementsByTagName('Relationship'))
    .filter((node) => (node.getAttribute('Type') || '').includes('/image'))
    .map((node) => node.getAttribute('Target') || '')
    .filter(Boolean);

  const images: Array<{ src: string; alt: string }> = [];

  for (const target of imageTargets) {
    const normalized = target.replace('../', '');
    const file = zip.file(`ppt/${normalized}`);

    if (!file) {
      continue;
    }

    const ext = normalized.split('.').pop()?.toLowerCase();
    const mimeType =
      ext === 'png'
        ? 'image/png'
        : ext === 'jpg' || ext === 'jpeg'
          ? 'image/jpeg'
          : ext === 'gif'
            ? 'image/gif'
            : ext === 'webp'
              ? 'image/webp'
              : null;

    if (!mimeType) {
      continue;
    }

    const base64 = await file.async('base64');
    images.push({
      src: `data:${mimeType};base64,${base64}`,
      alt: normalized.split('/').pop() || 'slide image',
    });
  }

  return images;
}

export async function readPptxSlides(file: File) {
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const slideFiles = Object.keys(zip.files)
    .filter((name) => /^ppt\/slides\/slide\d+\.xml$/.test(name))
    .sort((left, right) => {
      const leftNumber = Number(left.match(/slide(\d+)\.xml$/)?.[1] || 0);
      const rightNumber = Number(right.match(/slide(\d+)\.xml$/)?.[1] || 0);
      return leftNumber - rightNumber;
    });

  const slides: PptxSlidePreview[] = [];

  for (const slideFile of slideFiles) {
    const xml = await zip.file(slideFile)?.async('string');

    if (!xml) {
      continue;
    }

    const textLines = parseSlideText(xml);
    const images = await readSlideImages(zip, slideFile.replace(/^ppt\//, ''));
    slides.push({
      id: slideFile,
      title: textLines[0] || `Slide ${slides.length + 1}`,
      textLines,
      images,
    });
  }

  return slides;
}

export async function exportSlidesToPdf(slides: PptxSlidePreview[]) {
  const pdf = new jsPDF({
    unit: 'pt',
    orientation: 'landscape',
    format: [960, 540],
    compress: true,
  });

  for (let index = 0; index < slides.length; index += 1) {
    if (index > 0) {
      pdf.addPage([960, 540], 'landscape');
    }

    const slide = slides[index];
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, 960, 540, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(24);
    pdf.text(slide.title || `Slide ${index + 1}`, 40, 48);

    let cursorY = 88;
    if (slide.images[0]?.src) {
      try {
        pdf.addImage(
          slide.images[0].src,
          slide.images[0].src.includes('png') ? 'PNG' : 'JPEG',
          540,
          70,
          340,
          220,
        );
      } catch {
        // Ignore unsupported embedded images.
      }
    }

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(14);
    const textLines = slide.textLines.slice(1).join('\n') || slide.textLines.join('\n');
    const wrapped = pdf.splitTextToSize(textLines, 440);
    wrapped.forEach((line: string) => {
      if (cursorY > 490) {
        return;
      }
      pdf.text(line, 40, cursorY);
      cursorY += 20;
    });
  }

  return pdf.output('blob');
}
